import { CMS } from '../constants/cms'
import { parseSsn } from '../utils/ssn'
import { encryptToken } from '../utils/encrypt'
import { PAYMENT_REDIRECT_KEY } from '../constants/encryption'

import { normalizeRegionName, regionCode } from './addressParser'
import cmsAdapter from './cmsInstance'
import paymentAdapter, { getPaymentAdapterHeaders } from './paymentApiInstance'
import { getPinflagApiKey } from './utils'

export const parseProducts = (products) => (
  products.map(product => ({
    product_id: product.productId,
    sku: product.sku,
    name: product.productName,
    price: parseInt(product.lineTotal, 10),
    quantity: product.quantity,
    src: product.productImage,
    product_url: product.product
  }))
)

export const parseCustomerContact = (abandonedCartData, firstNameKey = 'firstName', lastNameKey = 'lastName', phoneValue = true, withoutDots = false) => {
  const [firstName, lastName] = abandonedCartData.names?.trim().split(/ (.+)/) || []

  return {
    email: abandonedCartData.email,
    [firstNameKey]: firstName,
    [lastNameKey]: lastName,
    phone: phoneValue ? abandonedCartData.phone : `56${abandonedCartData.phone}`,
    ssn: withoutDots ? parseSsn(abandonedCartData.rut, true) : parseSsn(abandonedCartData.rut)
  }
}

export const parseCustomerAddress = (abandonedCartData) => ({
  address: abandonedCartData.address,
  city: abandonedCartData.city,
  state: abandonedCartData.state,
  delivery_instructions: abandonedCartData.delivery_instructions
})

export const upsertAbandonedCart = async (cartData, customerData) => {
  const products = parseProducts(cartData.lines)

  try {
    const response = await cmsAdapter.post(
      '/abandoned-cart',
      {
        cart_id: cartData.referenceId,
        cart: { products, total: cartData.total },
        customer: {
          ...parseCustomerContact(customerData, 'name', 'last_name'),
          ...parseCustomerAddress(customerData)
        }
      }
    )

    return response.data
  } catch (error) {
    console.log('🚀 ~ file: pinflagRequest.js:61 ~ upsertAbandonedCart ~ error:', error)
  }
}

export const parseAddressData = (address, stateCodePrefix) => ({
  address: address.userAddress,
  city: address.district,
  deliveryInstructions: '',
  stateCode: `${stateCodePrefix}${regionCode[normalizeRegionName(address.region)]}`
})

export const parseVtexCartData = (data, vtexOrderFormId) => ({
  orderFormId: vtexOrderFormId,
  line_items: data.cart.lines.map(product => ({
    productId: product.id,
    quantity: product.quantity,
    sku: product.productName
  })),
  shipping_lines: [
    {
      method_code: data.selectedServiceType.courier,
      method_title: data.selectedServiceType.name,
      total: `${data.cart.shippingCost}`
    }
  ]
})

export const parseWoocomerceCartData = (cart) => ({
  referenceId: cart.referenceId,
  line_items: cart.lines.map(product => ({
    product_id: product.productId,
    quantity: product.quantity,
    sku: product.sku
  })),
  shipping_lines: [
    {
      method_id: 'flat_rate',
      method_title: 'Flat Rate',
      total: `${cart.shippingCost}`
    }
  ]
})

export const parseShopifyCartData = (cart, serviceType) => ({
  referenceId: cart.referenceId,
  line_items: cart.lines.map(product => ({
    variantId: product.variantId,
    requiresShipping: product.requires_shipping,
    quantity: product.quantity
  })),
  shipping_lines: [
    {
      method_code: serviceType.courier,
      method_title: serviceType.name,
      total: `${cart.shippingCost}`
    }
  ],
  total: cart.total
})

export const markShopifyOrderAsPaidRequest = async (cartData) => {
  const response = await cmsAdapter.post('/shopify/cart/mark-as-paid', { cart_token: cartData.cartId.split('?')[0] })
  return response.data
}

export const createPurcharseOrderRequest = async (purchaseOrderData) => {
  const pinflagApiKey = getPinflagApiKey()

  const response = await cmsAdapter.post('/order/place', purchaseOrderData, {
    headers: {
      'x-api-key': pinflagApiKey
    }
  })

  return response.data
}

export const checkIfCustomerHasSavedCardsRequest = async ({
  customer, paymentMethodsConfiguration
}) => {
  try {
    const response = await paymentAdapter.post('/check-saved-card-data', {
      customerData: parseCustomerContact(customer, 'name', 'lastName', true, true),
      paymentMethodsConfiguration
    })

    return response.data
  } catch (error) {
    throw new Error(error)
  }
}

export const getCustomerCardsDataRequest = async ({
  customer,
  paymentMethodsConfiguration,
  userToken
}) => {
  try {
    const response = await paymentAdapter.post('/saved-cards', {
      customerData: parseCustomerContact(customer, 'name', 'lastName', false),
      paymentMethodsConfiguration
    },
    {
      headers: getPaymentAdapterHeaders(userToken)
    })

    return response.data
  } catch (error) {
    throw new Error(error)
  }
}

export const deleteCustomerCardRequest = async ({
  cardId, customer, userToken
}) => {
  try {
    const response = await paymentAdapter.delete(`/saved-cards/${cardId}`, {
      data: {
        customerData: parseCustomerContact(customer, 'name', 'lastName', false, true),
        companyApiKey: getPinflagApiKey()
      },
      headers: getPaymentAdapterHeaders(userToken)
    })

    return response.data
  } catch (error) {
    console.log('Error:', error)
  }
}

export const getPaymentStatusByOrderId = async ({ orderId, gateway, force = false }) => {
  try {
    const url = `/payment/${orderId}/status?gateway=${gateway}&companyApiKey=${getPinflagApiKey()}${force ? '&force=true' : ''}`
    const response = await paymentAdapter.get(url)

    return response.data
  } catch (error) {
    console.log('Error:', error)
  }
}

export const createGuestPaymentOrderRequest = async (paymentOrderData) => {
  const body = { ...paymentOrderData, companyApiKey: getPinflagApiKey() }
  const response = await paymentAdapter.post('/payment/guest', body)
  return response.data
}

export const createOneClickPaymentOrderRequest = async (paymentOrderData, userToken) => {
  const body = { ...paymentOrderData, companyApiKey: getPinflagApiKey() }
  const response = await paymentAdapter.post('/payment/oneclick', body, { headers: getPaymentAdapterHeaders(userToken) })
  return response.data
}

export const subscribeCardRequest = async (subscriptionData) => {
  const body = { ...subscriptionData, companyApiKey: getPinflagApiKey() }
  const response = await paymentAdapter.post('/card-subscription', body)

  return response.data
}

export const finishCardSubscriptionRequest = async (finishCardSubscriptionData) => {
  const url = '/card-subscription/finish'
  const body = { ...finishCardSubscriptionData, companyApiKey: getPinflagApiKey() }
  const response = await paymentAdapter.post(url, body)
  return response.data
}

const getEcommerceRedirectUrl = (orderData, orderId, paymentMethod) => {
  const { cart, ecommerce } = orderData

  const domain = new URL(ecommerce.currentUrl).hostname
  const encryptedParams = encryptToken(
    `${cart.referenceId}|${orderId}|${paymentMethod}|${cart.shippingCost + cart.subtotal}`,
    PAYMENT_REDIRECT_KEY
  )

  const responseUrl = `https://${domain}/?referenceId=${encryptedParams}`

  return responseUrl
}

export const getTransbankCardInscriptionUrlRequest = async (orderData, orderId, paymentMethod) => {
  const { customer, cart } = orderData

  const responseUrl = getEcommerceRedirectUrl(orderData, orderId, paymentMethod)

  try {
    const cardInscriptionUrlData = {
      referenceId: cart.referenceId,
      orderId,
      paymentMethod: paymentMethod === 'debit' ? 'debitCard' : 'creditCard',
      customerData: parseCustomerContact(customer, 'name', 'lastName', true, true),
      responseUrl
    }
    console.log('✅✅ ~ getTransbankCardInscriptionUrl ~ cardInscriptionUrlData:', JSON.stringify(cardInscriptionUrlData, null, 2))

    const response = await paymentAdapter.post('/transbank/customer-enrollment/creation', cardInscriptionUrlData)
    console.log('✅✅ ~ getTransbankCardInscriptionUrl ~ response:', JSON.stringify(response, null, 2))

    return response.data
  } catch (error) {
    console.log('Error:', error)
  }
}

export const transbankSaveCardAndPayRequest = async (
  orderData, paymentMethod, referenceId, amount
) => {
  const { customer } = orderData

  try {
    const cardInscriptionUrlData = {
      referenceId,
      paymentMethod: paymentMethod === 'debit' ? 'debitCard' : 'creditCard',
      amount: `${amount}`,
      installments: 1,
      customerData: parseCustomerContact(customer, 'name', 'lastName', true, true)
    }

    const response = await paymentAdapter.post('/transbank/save-and-pay', cardInscriptionUrlData)

    return response.data
  } catch (error) {
    console.log('Error:', error)
  }
}

export const transbankMarkAsPaidRequest = async (cms, orderId) => {
  try {
    const response = await cmsAdapter.post('/order/mark-as-paid', {
      cms: CMS[cms],
      orderId
    })

    return response.data
  } catch (error) {
    console.log('Error:', error)
  }
}

export const getAbandonedCart = async (referenceId) => {
  try {
    const response = await cmsAdapter.get(`/abandoned-cart/${referenceId}`)

    return response.data
  } catch (error) {
    console.log('Error:', error)
  }
}

export const refundPaymentOrderRequest = async (body) => {
  const refundBody = {
    ...body,
    companyApiKey: getPinflagApiKey()
  }
  try {
    const response = await paymentAdapter.post('/refund', refundBody)
    return response.data
  } catch (error) {
    console.log('Error:', error)
  }
}
